/**
 * The parts of a skill the firm changes without touching the instructions,
 * as sections of the skill's Overview:
 *
 *   Triggers         schedules and events that run it on matching cases as
 *                    a chosen attorney, in Plan mode by default
 *   What It May Do   the mode it starts in, and the areas of work it
 *                    confirms with the attorney before acting on
 *
 * `useSkillSettingsDraft` keeps one draft of both and of the page's
 * words; `SkillSettingsSave` (in the skill's top bar) saves the changed parts
 * through `skills.update` with one change note, creates a new skill, or
 * discards them.
 */
import {
  CalendarClock,
  ChevronDown,
  ChevronRight,
  Hourglass,
  Plus,
  Trash2,
  Zap
} from "lucide-react";
import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type Dispatch,
  type FC,
  type ReactNode,
  type SetStateAction
} from "react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import { FilterChip } from "@/components/ui/filter-chip";
import { Input } from "@/components/ui/input";
import { Kbd } from "@/components/ui/kbd";
import { Label } from "@/components/ui/label";
import { SectionHeader } from "@/components/ui/page-header";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@/components/ui/select";
import { StatusBadge } from "@/components/ui/status-badge";
import { Spinner } from "@/components/ui/spinner";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from "@/components/ui/tooltip";
import { firmKnowledgeApi, type FirmScope } from "@/api/firm-knowledge";
import { useTeamMembers } from "@/features/agents/useTeamMembers";

import type { FirmSkillsState } from "@/features/firmKnowledge/hooks/useFirmSkills";
import { useSkillOptions } from "@/features/firmKnowledge/hooks/useSkillOptions";
import type {
  FirmSkill,
  NewSkillFields,
  SkillAuthority,
  SkillEdits,
  SkillOptions,
  SkillScheduleFreq,
  SkillTrigger,
  SkillTriggerEdit
} from "@/features/firmKnowledge/types";
import { canonicalVisa } from "@/features/firmKnowledge/visaCatalog";
import { VisaPicker } from "@/features/firmKnowledge/components/VisaPicker";
import {
  AutomationPreview,
  AutomationSentence,
  DependentsSwitch,
  ExpirationControls,
  WakeOverlapNote
} from "./AutomationEditor";
import {
  DATE_ACTIONS,
  EXPIRATION_ACTION,
  FREQ_LABEL,
  fullDate,
  isDateAction,
  PRIORITY_ACTION,
  shortDate,
  skillEvents,
  timezoneChoices,
  WEEKDAYS,
  whenLabel
} from "./skillModel";
import { AreaRow, groupAreas } from "./AuthorityAreas";
import { SAVE_SHORTCUT } from "./SkillSaveBar";
import { SuggestedAutomations } from "./SuggestedAutomations";

/** The browser's IANA zone, for a new schedule. */
export const browserTimezone = (): string => {
  try {
    return (
      Intl.DateTimeFormat().resolvedOptions().timeZone || "America/New_York"
    );
  } catch {
    return "America/New_York";
  }
};

type Setter<T> = Dispatch<SetStateAction<T>>;

/** A small label and hint above one outlined container; rows divide inside it. */
const Section: FC<{
  title: string;
  hint: string;
  children: ReactNode;
  testId: string;
  dirty?: boolean;
}> = ({ title, hint, children, testId, dirty }) => (
  // Unsaved state is shown once, in the top bar and the save bar, not per section.
  <section
    className="flex flex-col gap-3"
    data-testid={testId}
    data-dirty={dirty || undefined}
  >
    <SectionHeader title={title} description={hint} />
    <div className="flex flex-col divide-y divide-line-1 rounded-xl bg-elevated ring-1 ring-line-2">
      {children}
    </div>
  </section>
);

/** The last row of a section: a quiet way to add one more. */
const AddRow: FC<{ children: ReactNode }> = ({ children }) => (
  <div className="flex items-center px-2 py-1.5">{children}</div>
);

// -- What it may do -----------------------------------------------------------

export const AuthoritySection: FC<{
  draft: SkillSettingsDraft;
  canEdit: boolean;
}> = ({ draft, canEdit }) => {
  const { authority, setAuthority } = draft;
  const areas = draft.options?.confirm_before_areas ?? [];
  const setAskFirst = (area: string, askFirst: boolean) =>
    setAuthority((a) => ({
      ...a,
      confirm_before: askFirst
        ? a.confirm_before.includes(area)
          ? a.confirm_before
          : [...a.confirm_before, area]
        : a.confirm_before.filter((x) => x !== area)
    }));
  return (
    <Section
      title="What It May Do"
      hint="It does the work marked Allow on its own and waits for approval on anything marked Ask First. Anything that leaves the firm, and every connected app, always asks first."
      testId="skill-settings-authority"
      dirty={draft.dirty.authority}
    >
      <div className="space-y-1.5 p-3">
        <Label htmlFor="skill-default-mode" className="text-sm">
          When Someone Runs It
        </Label>
        <Select
          value={authority.default_mode}
          onValueChange={(v) =>
            setAuthority((a) => ({ ...a, default_mode: v as "agent" | "plan" }))
          }
          disabled={!canEdit}
        >
          <SelectTrigger
            id="skill-default-mode"
            size="sm"
            className="w-full max-w-80"
            data-testid="skill-default-mode"
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="agent">Start Working Right Away</SelectItem>
            <SelectItem value="plan">
              Write a Plan First, Then Wait for Approval
            </SelectItem>
          </SelectContent>
        </Select>
        <p className="text-sm text-fg-3" data-testid="skill-default-mode-help">
          Applies when someone runs this automation by hand. Each trigger has
          its own setting for the runs it starts.
        </p>
      </div>
      <div className="space-y-3 p-3" data-testid="skill-authority-areas">
        <div className="space-y-0.5">
          <Label className="text-sm">Approval for Each Kind of Work</Label>
          <p className="text-sm text-fg-3">
            Choose Ask First for anything you want to approve before it happens.
          </p>
        </div>
        {areas.length === 0 ? (
          <p className="text-sm text-fg-3">Loading areas…</p>
        ) : (
          groupAreas(areas).map((group) => (
            <div
              key={group.title}
              className="space-y-0.5"
              role="group"
              aria-label={group.title}
            >
              <p className="text-sm font-medium text-fg-2">{group.title}</p>
              <div className="flex flex-col divide-y divide-line-1">
                {group.areas.map((area) => (
                  <AreaRow
                    key={area}
                    area={area}
                    askFirst={authority.confirm_before.includes(area)}
                    disabled={!canEdit}
                    onChange={(askFirst) => setAskFirst(area, askFirst)}
                  />
                ))}
              </div>
            </div>
          ))
        )}
      </div>
      <div className="space-y-1.5 p-3">
        <Label htmlFor="skill-authority-notes" className="text-sm">
          Anything Else About What It May Do
        </Label>
        <Textarea
          id="skill-authority-notes"
          value={authority.notes}
          onChange={(e) =>
            setAuthority((a) => ({ ...a, notes: e.target.value }))
          }
          rows={2}
          maxLength={1000}
          disabled={!canEdit}
          placeholder="Never send anything to the client directly."
        />
      </div>
    </Section>
  );
};

// -- Automations --------------------------------------------------------------

const newTrigger = (
  kind: "schedule" | "event" | "date",
  runAs: string | null,
  events: readonly SkillOptions["events"][number][]
): SkillTriggerEdit => {
  const base: SkillTriggerEdit = {
    kind: kind === "schedule" ? "schedule" : "event",
    enabled: true,
    run_as_uid: runAs,
    mode: "plan",
    case_filter: { statuses: [], visa_types: [] },
    schedule:
      kind === "schedule"
        ? {
            freq: "weekly",
            time: "09:00",
            weekday: 0,
            day_of_month: 1,
            timezone: browserTimezone()
          }
        : null,
    event: null
  };
  if (kind === "event") {
    const first = events.find((e) => (e.kind ?? "activity") === "activity");
    base.event = { action: first?.action ?? "", cooldown_hours: 24 };
  }
  if (kind === "date") {
    const first = events.find((e) => e.kind === "date");
    const action = first?.action ?? "notice.response_due";
    base.event = {
      action,
      cooldown_hours: 24,
      days_before: first?.default_days ?? DATE_ACTIONS[action] ?? 14,
      kinds: [],
      include_dependents: true
    };
    base.autonomy = "propose";
    base.optimize_for = "cost";
  }
  return base;
};

/** "In Progress", "Filed": a case status as a label. */
const statusLabel = (status: string) =>
  status
    .replace(/_/g, " ")
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());

/** "A", "A and B", "A, B and C". */
const listOf = (items: string[]) =>
  items.length < 2
    ? items.join("")
    : `${items.slice(0, -1).join(", ")} and ${items[items.length - 1]}`;

/** One kind of trigger in the Add Trigger menu: icon, label and a one-line hint. */
const TriggerMenuItem: FC<{
  icon: typeof CalendarClock;
  label: string;
  hint: string;
  onSelect: () => void;
  testId: string;
}> = ({ icon: Icon, label, hint, onSelect, testId }) => (
  <DropdownMenuItem
    onSelect={onSelect}
    className="h-auto items-start gap-2.5 py-1.5"
    data-testid={testId}
  >
    <Icon className="mt-0.5 text-fg-3" />
    <span className="flex min-w-0 flex-col">
      <span className="whitespace-nowrap text-base text-fg">{label}</span>
      <span className="text-sm text-fg-3">{hint}</span>
    </span>
  </DropdownMenuItem>
);

export const TriggersSection: FC<{
  skill: FirmSkill;
  /** For the suggested automations shown while there are no triggers. */
  skills?: Pick<FirmSkillsState, "update">;
  draft: SkillSettingsDraft;
  canEdit: boolean;
  scope: FirmScope;
  isNew?: boolean;
}> = ({ skill, skills, draft, canEdit, scope, isNew = false }) => {
  const { triggers, setTriggers, options } = draft;
  const { members: team, me } = useTeamMembers();
  const members = team.map((m) => ({ uid: m.uid, label: m.label }));
  const meUid = me.uid;
  const events = skillEvents(options);
  const hasDateEvents = events.some((e) => e.kind === "date");
  const bookkeeping = useMemo(
    () =>
      new Map(skill.triggers.map((t) => [t.id, t] as [string, SkillTrigger])),
    [skill.triggers]
  );
  const defaultRunAs = skill.created_by ?? meUid ?? null;
  const memberName = (uid: string | null) =>
    members.find((m) => m.uid === uid)?.label ?? "the automation's creator";
  // Saved triggers start collapsed, new ones open.
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  const edit = (i: number, patch: Partial<SkillTriggerEdit>) =>
    setTriggers((cur) => cur.map((t, j) => (j === i ? { ...t, ...patch } : t)));

  const summaryOf = (t: SkillTriggerEdit, date: boolean): string => {
    const parts: string[] = [];
    if (t.kind === "event" && !date) {
      const hours = t.event?.cooldown_hours ?? 24;
      parts.push(hours > 0 ? `Once per case per ${hours}h` : "Every time");
    }
    parts.push(`Acts as ${memberName(t.run_as_uid ?? defaultRunAs)}`);
    const statuses = t.case_filter?.statuses ?? [];
    parts.push(
      statuses.length ? listOf(statuses.map(statusLabel)) : "Open cases"
    );
    const visas = t.case_filter?.visa_types ?? [];
    parts.push(
      visas.length
        ? visas.map((v) => canonicalVisa(v) ?? v).join(", ")
        : "Any visa"
    );
    return parts.join(" · ");
  };

  const suggestions =
    canEdit && !isNew && skills && skill.suggested_automations?.length;

  return (
    <Section
      title="Triggers"
      hint="Run this automation without being asked: on a schedule, when something happens on a case, or as a deadline or expiration date gets close. Automated runs write a plan first and wait for approval unless you change that."
      testId="skill-settings-automations"
      dirty={draft.dirty.triggers}
    >
      {triggers.length === 0 ? (
        suggestions ? (
          <SuggestedAutomations skill={skill} skills={skills} />
        ) : (
          <p className="px-3 py-2.5 text-base text-fg-3">
            No triggers. The automation runs only when someone runs it.
          </p>
        )
      ) : null}
      {triggers.map((t, i) => {
        const saved = t.id ? bookkeeping.get(t.id) : undefined;
        const date = t.kind === "event" && isDateAction(t.event?.action);
        const Icon =
          t.kind === "schedule" ? CalendarClock : date ? Hourglass : Zap;
        const key = t.id || `new-${i}`;
        const open = expanded[key] ?? !t.id;
        const statuses = t.case_filter?.statuses ?? [];
        return (
          <div
            key={key}
            className="space-y-3 p-3"
            data-testid="skill-trigger-row"
            data-kind={date ? "date" : t.kind}
          >
            <div className="flex items-start gap-2.5">
              <Icon className="mt-1.5 size-4 shrink-0 text-fg-3" />
              <div className="flex min-w-0 flex-1 flex-col">
                <AutomationSentence
                  trigger={t}
                  onChange={(patch) => edit(i, patch)}
                  options={options}
                  canEdit={canEdit}
                />
                {saved &&
                (saved.next_fire_at ||
                  saved.last_fired_at ||
                  saved.last_error) ? (
                  <p
                    className="text-sm text-fg-3"
                    data-testid="skill-trigger-status"
                  >
                    {saved.next_fire_at ? (
                      <span title={fullDate(saved.next_fire_at)}>
                        Next run {whenLabel(saved.next_fire_at)}
                      </span>
                    ) : null}
                    {saved.next_fire_at && saved.last_fired_at ? " · " : null}
                    {saved.last_fired_at ? (
                      <span title={fullDate(saved.last_fired_at)}>
                        Last ran {shortDate(saved.last_fired_at)}
                      </span>
                    ) : null}
                    {saved.last_error ? (
                      <span className="text-danger"> {saved.last_error}</span>
                    ) : null}
                  </p>
                ) : null}
              </div>
              <div className="flex shrink-0 items-center gap-1 pt-1">
                <Switch
                  id={`skill-trigger-enabled-${i}`}
                  checked={t.enabled !== false}
                  onCheckedChange={(v) => edit(i, { enabled: v })}
                  disabled={!canEdit}
                  aria-label={
                    t.enabled !== false
                      ? "Turn the trigger off"
                      : "Turn the trigger on"
                  }
                  data-testid="skill-trigger-enabled"
                />
                {canEdit ? (
                  <Button
                    type="button"
                    size="icon-xs"
                    variant="ghost"
                    aria-label="Remove Trigger"
                    onClick={() =>
                      setTriggers((cur) => cur.filter((_, j) => j !== i))
                    }
                  >
                    <Trash2 className="size-3.5" />
                  </Button>
                ) : null}
              </div>
            </div>
            <div className="flex min-w-0 items-center gap-2 pl-6.5">
              <Button
                type="button"
                variant="ghost"
                size="xs"
                className="-ml-2 text-fg-2"
                aria-expanded={open}
                onClick={() => setExpanded((cur) => ({ ...cur, [key]: !open }))}
                data-testid="skill-trigger-details"
              >
                {open ? <ChevronDown /> : <ChevronRight />} Details
              </Button>
              {open ? null : (
                <span
                  className="min-w-0 truncate text-sm text-fg-3"
                  data-testid="skill-trigger-summary"
                >
                  {summaryOf(t, date)}
                </span>
              )}
            </div>

            {open ? (
              <div
                className="space-y-3 pl-6.5"
                data-testid="skill-trigger-details-body"
              >
                {t.kind === "schedule" && t.schedule ? (
                  <div className="grid gap-2 sm:grid-cols-4">
                    <div className="space-y-1">
                      <Label className="text-sm">How Often</Label>
                      <Select
                        value={t.schedule.freq}
                        onValueChange={(v) =>
                          edit(i, {
                            schedule: {
                              ...t.schedule!,
                              freq: v as SkillScheduleFreq
                            }
                          })
                        }
                        disabled={!canEdit}
                      >
                        <SelectTrigger
                          size="sm"
                          data-testid="skill-schedule-freq"
                        >
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {(
                            options?.schedule_frequencies ??
                            Object.keys(FREQ_LABEL)
                          ).map((f) => (
                            <SelectItem key={f} value={f}>
                              {FREQ_LABEL[f as SkillScheduleFreq] ?? f}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    {t.schedule.freq === "weekly" ? (
                      <div className="space-y-1">
                        <Label className="text-sm">Day</Label>
                        <Select
                          value={String(t.schedule.weekday)}
                          onValueChange={(v) =>
                            edit(i, {
                              schedule: { ...t.schedule!, weekday: Number(v) }
                            })
                          }
                          disabled={!canEdit}
                        >
                          <SelectTrigger size="sm">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {WEEKDAYS.map((d, n) => (
                              <SelectItem key={d} value={String(n)}>
                                {d}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    ) : null}
                    {t.schedule.freq === "monthly" ? (
                      <div className="space-y-1">
                        <Label className="text-sm">Day of Month</Label>
                        <Input
                          type="number"
                          min={1}
                          max={28}
                          value={t.schedule.day_of_month}
                          onChange={(e) =>
                            edit(i, {
                              schedule: {
                                ...t.schedule!,
                                day_of_month: Math.min(
                                  28,
                                  Math.max(1, Number(e.target.value) || 1)
                                )
                              }
                            })
                          }
                          disabled={!canEdit}
                          className="h-8"
                        />
                      </div>
                    ) : null}
                    <div className="space-y-1">
                      <Label className="text-sm">Time</Label>
                      <Input
                        type="time"
                        value={t.schedule.time}
                        onChange={(e) =>
                          edit(i, {
                            schedule: { ...t.schedule!, time: e.target.value }
                          })
                        }
                        disabled={!canEdit}
                        className="h-8"
                        data-testid="skill-schedule-time"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-sm">Timezone</Label>
                      <Select
                        value={t.schedule.timezone}
                        onValueChange={(v) =>
                          edit(i, {
                            schedule: { ...t.schedule!, timezone: v }
                          })
                        }
                        disabled={!canEdit}
                      >
                        <SelectTrigger
                          size="sm"
                          data-testid="skill-schedule-timezone"
                        >
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {timezoneChoices(
                            t.schedule.timezone,
                            browserTimezone()
                          ).map((z) => (
                            <SelectItem key={z.value} value={z.value}>
                              {z.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                ) : null}

                {t.kind === "event" && t.event && !date ? (
                  <div className="space-y-1 sm:w-1/2">
                    <Label className="text-sm">
                      At Most Once per Case Every (Hours)
                    </Label>
                    <Input
                      type="number"
                      min={0}
                      max={720}
                      value={t.event.cooldown_hours ?? 24}
                      onChange={(e) =>
                        edit(i, {
                          event: {
                            ...t.event!,
                            cooldown_hours: Math.max(
                              0,
                              Number(e.target.value) || 0
                            )
                          }
                        })
                      }
                      disabled={!canEdit}
                      className="h-8"
                    />
                  </div>
                ) : null}

                {date && t.event?.action === EXPIRATION_ACTION ? (
                  <>
                    <ExpirationControls
                      trigger={t}
                      onChange={(patch) => edit(i, patch)}
                      options={options}
                      canEdit={canEdit}
                      index={i}
                    />
                    {options?.assistant_expiration_wake ? (
                      <WakeOverlapNote
                        lawFirmId={skill.law_firm_id}
                        canEdit={canEdit}
                      />
                    ) : null}
                  </>
                ) : null}
                {date && t.event?.action === PRIORITY_ACTION ? (
                  <DependentsSwitch
                    trigger={t}
                    onChange={(patch) => edit(i, patch)}
                    canEdit={canEdit}
                    index={i}
                  />
                ) : null}

                <div className="grid gap-2 sm:grid-cols-2">
                  <div className="space-y-1">
                    <Label className="text-sm">Acts As</Label>
                    <Select
                      value={t.run_as_uid ?? "__creator__"}
                      onValueChange={(v) =>
                        edit(i, { run_as_uid: v === "__creator__" ? null : v })
                      }
                      disabled={!canEdit}
                    >
                      <SelectTrigger
                        size="sm"
                        data-testid="skill-trigger-run-as"
                      >
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__creator__">
                          The automation's creator ({memberName(defaultRunAs)})
                        </SelectItem>
                        {members.map((m) => (
                          <SelectItem key={m.uid} value={m.uid}>
                            {m.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-sm text-fg-3">
                      Runs with this person's access. They are told when each
                      run finishes or needs them, with a morning summary of what
                      ran overnight.
                    </p>
                  </div>
                </div>

                <div className="space-y-1.5">
                  <Label className="text-sm">
                    {t.kind === "schedule"
                      ? "Which Cases"
                      : "Only on Cases That Are"}
                  </Label>
                  <div className="flex flex-wrap gap-1.5">
                    {(options?.case_statuses ?? []).map((status) => {
                      const picked = statuses.includes(status);
                      return (
                        <FilterChip
                          key={status}
                          active={picked}
                          disabled={!canEdit}
                          onClick={() =>
                            edit(i, {
                              case_filter: {
                                statuses: picked
                                  ? statuses.filter((s) => s !== status)
                                  : [...statuses, status],
                                visa_types: t.case_filter?.visa_types ?? []
                              }
                            })
                          }
                          data-testid={`skill-trigger-status-${status}`}
                        >
                          {statusLabel(status)}
                        </FilterChip>
                      );
                    })}
                  </div>
                  <p
                    className="text-sm text-fg-3"
                    data-testid="skill-trigger-statuses-help"
                  >
                    {statuses.length
                      ? `Only ${listOf(statuses.map(statusLabel))} cases.`
                      : "Every open case; closed ones are skipped."}
                  </p>
                  <div className="space-y-1">
                    <Label
                      className="text-sm"
                      htmlFor={`skill-trigger-${i}-visas`}
                    >
                      Visas
                    </Label>
                    <VisaPicker
                      id={`skill-trigger-${i}-visas`}
                      multiple
                      value={(t.case_filter?.visa_types ?? []).map(
                        (visa) => canonicalVisa(visa) ?? visa
                      )}
                      onChange={(next) =>
                        edit(i, {
                          case_filter: {
                            statuses: t.case_filter?.statuses ?? [],
                            visa_types: next
                          }
                        })
                      }
                      disabled={!canEdit}
                      className="h-8"
                      placeholder="Any visa"
                    />
                  </div>
                </div>
              </div>
            ) : null}

            {date && t.enabled !== false && skill.id ? (
              <AutomationPreview skillId={skill.id} trigger={t} scope={scope} />
            ) : null}
          </div>
        );
      })}
      {canEdit && triggers.length < 10 ? (
        <AddRow>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                type="button"
                size="sm"
                variant="ghost"
                className="text-fg-2"
                data-testid="skill-trigger-add"
              >
                <Plus className="size-3.5" /> Add Trigger
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="start"
              className="w-80 max-w-(--radix-dropdown-menu-content-available-width)"
              onCloseAutoFocus={(e) => e.preventDefault()}
              data-testid="skill-trigger-menu"
            >
              <TriggerMenuItem
                icon={CalendarClock}
                label="Schedule"
                hint="Run at a set time, like every Monday."
                onSelect={() =>
                  setTriggers((cur) => [
                    ...cur,
                    newTrigger("schedule", null, events)
                  ])
                }
                testId="skill-trigger-add-schedule"
              />
              <TriggerMenuItem
                icon={Zap}
                label="When Something Happens"
                hint="Run when a case changes."
                onSelect={() =>
                  setTriggers((cur) => [
                    ...cur,
                    newTrigger("event", null, events)
                  ])
                }
                testId="skill-trigger-add-event"
              />
              {hasDateEvents ? (
                <TriggerMenuItem
                  icon={Hourglass}
                  label="Before a Date"
                  hint="Run ahead of a deadline or expiration."
                  onSelect={() =>
                    setTriggers((cur) => [
                      ...cur,
                      newTrigger("date", null, events)
                    ])
                  }
                  testId="skill-trigger-add-date"
                />
              ) : null}
            </DropdownMenuContent>
          </DropdownMenu>
        </AddRow>
      ) : null}
    </Section>
  );
};

// -- The draft ----------------------------------------------------------------

const SECTION_NOTE = {
  content: "the instructions",
  authority: "what it may do on its own",
  triggers: "the automations",
  visas: "the visas"
} as const;

type SectionKey = keyof typeof SECTION_NOTE;

/**
 * The words on the skill page: its title and instructions, and the
 * description the Edit Description dialog changes.
 */
export type SkillContent = Pick<
  FirmSkill,
  "title" | "description" | "instructions"
> & {
  /** CaseBlink writes the description (on save, from the other words). */
  description_auto: boolean;
  /** What a new skill is created as; a saved one changes through its switch. */
  status: FirmSkill["status"];
};

/** The visas the automation is for: as set, else as its title names; none is every case. */
const visasOf = (skill: FirmSkill): string[] =>
  skill.visas ?? skill.visa_types ?? [];

const contentOf = (skill: FirmSkill): SkillContent => ({
  title: skill.title,
  description: skill.description,
  description_auto: Boolean(skill.description_auto),
  instructions: skill.instructions,
  status: skill.status
});

/** What a new skill is measured against: nothing yet, so a starter's prefill is unsaved. */
const NOTHING: Pick<FirmSkill, "authority" | "triggers"> & {
  content: SkillContent;
  visas: string[];
} = {
  content: {
    title: "",
    description: "",
    description_auto: false,
    instructions: "",
    status: "active"
  },
  authority: { default_mode: "agent", confirm_before: [], notes: "" },
  triggers: [],
  visas: []
};

/** A trigger as the create path sends it: no id or bookkeeping. */
const unsavedTrigger = (t: SkillTriggerEdit): SkillTriggerEdit => ({
  kind: t.kind,
  enabled: t.enabled,
  run_as_uid: t.run_as_uid,
  mode: t.mode,
  case_filter: t.case_filter,
  schedule: t.schedule,
  event: t.event,
  optimize_for: t.optimize_for,
  autonomy: t.autonomy
});

type SaveExtra = {
  status?: FirmSkill["status"];
  /** A new skill's caller says it was created (its own toast). */
  quiet?: boolean;
};

export type SkillSettingsDraft = {
  content: SkillContent;
  setContent: Setter<SkillContent>;
  authority: SkillAuthority;
  setAuthority: Setter<SkillAuthority>;
  triggers: SkillTriggerEdit[];
  setTriggers: Setter<SkillTriggerEdit[]>;
  /** The visas the automation is for (canonical names); none is any visa. */
  visas: string[];
  setVisas: Setter<string[]>;
  /** Not saved yet: the first Save creates it. */
  isNew: boolean;
  dirty: Record<SectionKey, boolean>;
  changed: SectionKey[];
  /**
   * The saved (or just created) skill; null when the save failed. `status`
   * saves a status change with the rest (the switch on unsaved words).
   */
  save: (extra?: SaveExtra) => Promise<FirmSkill | null>;
  discard: () => void;
  saving: boolean;
  options: SkillOptions | null;
  /** Library entry and skill ids the instructions point at (`@` mentions). */
  setMentioned: (mentioned: MentionedIds) => void;
};

/** What the instructions' `@` mentions point at, added to the skill on save. */
export type MentionedIds = { entries: string[]; skills: string[] };

type StoredDraft = {
  content: SkillContent;
  authority: SkillAuthority;
  triggers: SkillTriggerEdit[];
  visas?: string[];
};

/** Where a new skill's unsaved draft waits (this tab only). */
export const newSkillDraftKey = (lawFirmId: string, key: string): string =>
  `firm-skill-draft:${lawFirmId}:new:${key}`;

const readStoredDraft = (key: string | null): StoredDraft | null => {
  if (!key) return null;
  try {
    const raw = window.sessionStorage.getItem(key);
    return raw ? (JSON.parse(raw) as StoredDraft) : null;
  } catch {
    return null;
  }
};

const writeStoredDraft = (key: string, draft: StoredDraft | null) => {
  try {
    if (draft) window.sessionStorage.setItem(key, JSON.stringify(draft));
    else window.sessionStorage.removeItem(key);
  } catch {
    // Private mode or a full quota: the in-memory draft still stands.
  }
};

/** Library documents and skills an `@` mention adds, not yet on the skill. */
export const mentionAdditions = (
  mentioned: MentionedIds,
  references: Array<{ entry_id: string }>,
  uses: Array<{ skill_id: string }>,
  selfId: string
): { references: string[]; uses: string[] } => ({
  references: mentioned.entries.filter(
    (id) => !references.some((r) => r.entry_id === id)
  ),
  uses: mentioned.skills.filter(
    (id) => id !== selfId && !uses.some((u) => u.skill_id === id)
  )
});

/**
 * One draft of the whole skill page (its words, triggers and
 * authority), saved together. For a new skill (`isNew`) the first Save
 * creates it, then saves its triggers (the create endpoint takes none);
 * until then the draft also waits in sessionStorage under `draftKey`, so a
 * reload or a wrong turn doesn't lose it.
 */
export const useSkillSettingsDraft = (
  skill: FirmSkill,
  skills: FirmSkillsState,
  scope: FirmScope,
  { isNew = false, draftKey }: { isNew?: boolean; draftKey?: string } = {}
): SkillSettingsDraft => {
  const options = useSkillOptions(scope);
  const storeKey =
    isNew && draftKey ? newSkillDraftKey(skill.law_firm_id, draftKey) : null;
  const [stored] = useState(() => readStoredDraft(storeKey));
  const [content, setContent] = useState<SkillContent>(
    () => stored?.content ?? contentOf(skill)
  );
  const [authority, setAuthority] = useState<SkillAuthority>(
    () => stored?.authority ?? skill.authority
  );
  const [triggers, setTriggers] = useState<SkillTriggerEdit[]>(
    () => stored?.triggers ?? skill.triggers
  );
  const [visas, setVisas] = useState<string[]>(
    () => stored?.visas ?? visasOf(skill)
  );
  const [saving, setSaving] = useState(false);
  const [mentioned, setMentioned] = useState<MentionedIds>({
    entries: [],
    skills: []
  });

  useEffect(() => {
    if (!storeKey) return;
    const pristine =
      JSON.stringify(content) === JSON.stringify(contentOf(skill)) &&
      JSON.stringify(authority) === JSON.stringify(skill.authority) &&
      JSON.stringify(triggers) === JSON.stringify(skill.triggers) &&
      JSON.stringify(visas) === JSON.stringify(visasOf(skill));
    writeStoredDraft(
      storeKey,
      pristine ? null : { content, authority, triggers, visas }
    );
  }, [storeKey, skill, content, authority, triggers, visas]);

  // A save, an enable or the builder's write changes the skill: take what
  // changed into the draft (not on mount, so a restored draft stands).
  const seen = useRef(skill);
  useEffect(() => {
    const prev = seen.current;
    seen.current = skill;
    if (
      prev.title !== skill.title ||
      prev.description !== skill.description ||
      prev.description_auto !== skill.description_auto ||
      prev.instructions !== skill.instructions
    ) {
      setContent((c) => ({
        ...c,
        title: skill.title,
        description: skill.description,
        description_auto: Boolean(skill.description_auto),
        instructions: skill.instructions
      }));
    }
    if (prev.status !== skill.status) {
      setContent((c) => ({ ...c, status: skill.status }));
    }
    if (prev.authority !== skill.authority) setAuthority(skill.authority);
    if (prev.triggers !== skill.triggers) setTriggers(skill.triggers);
    if (JSON.stringify(visasOf(prev)) !== JSON.stringify(visasOf(skill))) {
      setVisas(visasOf(skill));
    }
  }, [skill]);

  const base = isNew
    ? NOTHING
    : {
        content: contentOf(skill),
        authority: skill.authority,
        triggers: skill.triggers,
        visas: visasOf(skill)
      };
  const dirty: Record<SectionKey, boolean> = {
    content: JSON.stringify(content) !== JSON.stringify(base.content),
    authority: JSON.stringify(authority) !== JSON.stringify(base.authority),
    triggers: JSON.stringify(triggers) !== JSON.stringify(base.triggers),
    visas: JSON.stringify(visas) !== JSON.stringify(base.visas)
  };
  const changed = (Object.keys(dirty) as SectionKey[]).filter((k) => dirty[k]);

  const discard = () => {
    setContent(contentOf(skill));
    setAuthority(skill.authority);
    setTriggers(skill.triggers);
    setVisas(visasOf(skill));
    if (storeKey) writeStoredDraft(storeKey, null);
  };

  // `@` mentions in the instructions attach what they point at on save.
  const additions = mentionAdditions(
    mentioned,
    skill.references,
    skill.uses_skills,
    skill.id
  );
  const references: NonNullable<SkillEdits["references"]> = [
    ...skill.references.map(({ entry_id, role, note }) => ({
      entry_id,
      role,
      note
    })),
    ...additions.references.map((entry_id) => ({
      entry_id,
      role: "exemplar" as const
    }))
  ];
  const usesSkills = [
    ...skill.uses_skills.map((u) => u.skill_id),
    ...additions.uses
  ];

  const create = async (
    status: FirmSkill["status"] = content.status
  ): Promise<FirmSkill> => {
    const fields: NewSkillFields = {
      title: content.title.trim() || "Untitled Automation",
      instructions: content.instructions,
      status,
      // The questions a run asks aren't edited here; a starter's or a copy's
      // carry over as they are.
      inputs: skill.inputs,
      authority
    };
    // Left out, the backend writes it.
    if (content.description.trim() && !content.description_auto) {
      fields.description = content.description;
    }
    if (references.length) fields.references = references;
    if (usesSkills.length) fields.uses_skills = usesSkills;
    if (skill.starter_id) fields.starter_id = skill.starter_id;
    const created = await firmKnowledgeApi.createSkill(fields, scope);
    skills.replaceSkill(created);
    // The create endpoint takes neither triggers nor visas: a second write.
    if (!triggers.length && !visas.length) return created;
    const followUp: SkillEdits = {};
    if (visas.length) followUp.visa_types = visas;
    if (triggers.length) followUp.triggers = triggers.map(unsavedTrigger);
    followUp.change_note =
      triggers.length && visas.length
        ? "Added the automations and the visas"
        : triggers.length
        ? "Added the automations"
        : "Added the visas";
    return skills.update(created.id, followUp);
  };

  const save = async (extra: SaveExtra = {}): Promise<FirmSkill | null> => {
    setSaving(true);
    try {
      if (isNew) {
        const created = await create(extra.status);
        if (storeKey) writeStoredDraft(storeKey, null);
        if (!extra.quiet) toast.success("Automation saved");
        return created;
      }
      const notes: string[] = changed.map((k) => SECTION_NOTE[k]);
      if (extra.status && extra.status !== skill.status) {
        notes.push(extra.status === "active" ? "enabled it" : "archived it");
      }
      const edits: SkillEdits = {
        change_note: `Changed ${notes.join(" and ")}`
      };
      if (dirty.content) {
        edits.title = content.title.trim() || skill.title;
        // Sending the description, even unchanged, marks it hand-written.
        if (content.description_auto && !skill.description_auto) {
          edits.description_auto = true;
        } else if (content.description !== skill.description) {
          edits.description = content.description;
        }
        edits.instructions = content.instructions;
        if (additions.references.length) edits.references = references;
        if (additions.uses.length) edits.uses_skills = usesSkills;
      }
      // `inputs` is never sent for a saved automation: its questions stay as
      // they are.
      if (dirty.authority) edits.authority = authority;
      if (dirty.triggers) edits.triggers = triggers;
      if (dirty.visas) edits.visa_types = visas;
      if (extra.status) edits.status = extra.status;
      const saved = await skills.update(skill.id, edits);
      toast.success("Automation saved");
      const enabled = (extra.status ?? skill.status) === "active";
      if (dirty.triggers && triggers.length && !enabled) {
        toast.message("Automations run once the automation is enabled.");
      }
      return saved;
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Could not save.");
      return null;
    } finally {
      setSaving(false);
    }
  };

  return {
    content,
    setContent,
    authority,
    setAuthority,
    triggers,
    setTriggers,
    visas,
    setVisas,
    isNew,
    dirty,
    changed,
    save,
    discard,
    saving,
    options,
    setMentioned
  };
};

/**
 * Cursor's top-bar Save: always there for editors, live (primary) once
 * something changed (a new skill's prefill counts), with Discard and an
 * "Unsaved" marker beside it then. The page's save bar repeats it at the
 * bottom of the editor.
 * `onDiscard` replaces the reset (a new skill's Discard leaves the page);
 * `onSave` replaces the save (the page checks the instructions first).
 */
export const SkillSettingsSave: FC<{
  draft: SkillSettingsDraft;
  canEdit: boolean;
  onSaved?: (skill: FirmSkill) => void;
  onDiscard?: () => void;
  onSave?: () => void;
}> = ({ draft, canEdit, onSaved, onDiscard, onSave }) => {
  if (!canEdit) return null;
  const live = draft.changed.length > 0;
  return (
    <div
      className="flex min-w-0 items-center gap-1.5"
      data-testid="skill-settings-bar"
      data-dirty={live || undefined}
    >
      {live ? (
        <>
          <StatusBadge tone="warn" dot data-testid="skill-unsaved-indicator">
            Unsaved
          </StatusBadge>
          <Button
            size="sm"
            variant="ghost"
            disabled={draft.saving}
            onClick={onDiscard ?? draft.discard}
            data-testid="skill-settings-discard"
          >
            Discard
          </Button>
        </>
      ) : null}
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant={live ? "primary" : "tonal"}
              size="sm"
              disabled={draft.saving || !live}
              onClick={async () => {
                if (onSave) {
                  onSave();
                  return;
                }
                const saved = await draft.save();
                if (saved) onSaved?.(saved);
              }}
              data-testid="skill-settings-save"
            >
              {draft.saving ? <Spinner className="size-3.5" /> : null}
              {draft.saving ? "Saving…" : "Save"}
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            Save <Kbd>{SAVE_SHORTCUT}</Kbd>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    </div>
  );
};
